import { useState, useEffect, useCallback } from 'react';
import { Spin, Empty, Modal } from 'antd';
import { LinkOutlined, PictureOutlined, PlayCircleOutlined, FileOutlined, DownloadOutlined } from '@ant-design/icons';
import axios from 'axios';
import { baseUrl } from '../../../config';
import { authHeader, useLang } from '../shared/utils';
import { M } from '../shared/constants';

// Extract Drive file ID from various link formats
function parseDriveId(link) {
  if (!link) return null;
  const m =
    link.match(/\/file\/d\/([a-zA-Z0-9_-]+)/) ||
    link.match(/id=([a-zA-Z0-9_-]+)/) ||
    link.match(/\/folders\/([a-zA-Z0-9_-]+)/);
  return m ? m[1] : link.trim();
}

function driveThumbnail(link, type) {
  const id = parseDriveId(link);
  if (!id) return null;
  if (type === 'image') return `https://drive.google.com/thumbnail?id=${id}&sz=w400`;
  if (type === 'video') return `https://drive.google.com/thumbnail?id=${id}&sz=w400`;
  return null;
}

function driveEmbed(link, type) {
  const id = parseDriveId(link);
  if (!id) return link;
  if (type === 'image')  return `https://drive.google.com/uc?export=view&id=${id}`;
  if (type === 'video')  return `https://drive.google.com/file/d/${id}/preview`;
  if (type === 'document') return `https://drive.google.com/file/d/${id}/preview`;
  if (type === 'album' || type === 'folder')
    return `https://drive.google.com/embeddedfolderview?id=${id}#grid`;
  return `https://drive.google.com/file/d/${id}/preview`;
}

const TYPE_ICON = {
  image:    <PictureOutlined />,
  video:    <PlayCircleOutlined style={{ color: '#dc2626' }} />,
  document: <FileOutlined style={{ color: '#1d4ed8' }} />,
  album:    <PictureOutlined style={{ color: '#7c3aed' }} />,
  folder:   <LinkOutlined style={{ color: '#059669' }} />,
};

export function GalleryTab() {
  const { t } = useLang();
  const [items,      setItems]      = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [catFilter,  setCatFilter]  = useState(null);
  const [page,       setPage]       = useState(1);
  const [total,      setTotal]      = useState(0);
  const [active,     setActive]     = useState(null); // item open in modal

  const LIMIT = 20;

  const load = useCallback(async (pg = 1, cat = catFilter) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: pg, limit: LIMIT });
      if (cat) params.set('category', cat);
      const r = await axios.get(`${baseUrl}/member/gallery?${params}`, { headers: authHeader() });
      if (r.data.status) {
        if (pg === 1) { setItems(r.data.result || []); } else { setItems(prev => [...prev, ...(r.data.result || [])]); }
        setTotal(r.data.total || 0);
        if (pg === 1) setCategories(r.data.categories || []);
        setPage(pg);
      }
    } catch {} finally { setLoading(false); }
  }, [catFilter]);

  useEffect(() => { load(1); }, [load]);

  const selectCat = (cat) => {
    setCatFilter(cat);
    setItems([]);
    load(1, cat);
  };

  return (
    <div style={{ padding: '0 0 80px' }}>
      {/* Header */}
      <div style={{ background: `linear-gradient(135deg, ${M}, #b71c1c)`, borderRadius: 14, padding: '16px 18px', marginBottom: 14 }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: 'rgba(255,255,255,0.65)', textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 4 }}>
          {t('समाज गैलरी', 'Samaj Gallery')}
        </div>
        <div style={{ fontSize: 20, fontWeight: 900, color: '#FDE68A' }}>
          {t('फ़ोटो, वीडियो और दस्तावेज़', 'Photos, Videos & Documents')}
        </div>
        <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.7)', marginTop: 4 }}>
          {t('Google Drive से सीधे देखें और डाउनलोड करें', 'View & download directly from Google Drive')}
        </div>
      </div>

      {/* Category filter pills */}
      {categories.length > 0 && (
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 14 }}>
          <button
            onClick={() => selectCat(null)}
            style={{
              borderRadius: 20, padding: '5px 14px', fontSize: 12, fontWeight: 700,
              border: `1.5px solid ${!catFilter ? M : '#e5e7eb'}`,
              background: !catFilter ? M : '#fff',
              color: !catFilter ? '#FDE68A' : '#374151', cursor: 'pointer',
            }}>
            {t('सभी', 'All')}
          </button>
          {categories.map(c => (
            <button key={c}
              onClick={() => selectCat(catFilter === c ? null : c)}
              style={{
                borderRadius: 20, padding: '5px 14px', fontSize: 12, fontWeight: 700, textTransform: 'capitalize',
                border: `1.5px solid ${catFilter === c ? M : '#e5e7eb'}`,
                background: catFilter === c ? M : '#fff',
                color: catFilter === c ? '#FDE68A' : '#374151', cursor: 'pointer',
              }}>
              {c}
            </button>
          ))}
        </div>
      )}

      {/* Grid */}
      {loading && items.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 48 }}><Spin /></div>
      ) : items.length === 0 ? (
        <Empty description={t('अभी कोई गैलरी आइटम नहीं है', 'No gallery items yet')} />
      ) : (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 10 }}>
            {items.map(item => {
              const thumb = item.thumbnail_url || driveThumbnail(item.drive_link, item.media_type);
              return (
                <div
                  key={item.id}
                  onClick={() => setActive(item)}
                  style={{
                    borderRadius: 12, overflow: 'hidden', cursor: 'pointer',
                    border: '1px solid #e5e7eb', background: '#fff',
                    boxShadow: '0 1px 6px rgba(0,0,0,0.06)',
                    transition: 'transform 0.15s',
                  }}
                  onMouseEnter={e => (e.currentTarget.style.transform = 'scale(1.02)')}
                  onMouseLeave={e => (e.currentTarget.style.transform = 'scale(1)')}
                >
                  {/* Thumbnail */}
                  <div style={{ position: 'relative', paddingTop: '66%', background: '#f3f4f6', overflow: 'hidden' }}>
                    {thumb ? (
                      <img
                        src={thumb}
                        alt={item.title_hi}
                        style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }}
                        onError={e => { e.currentTarget.style.display = 'none'; }}
                      />
                    ) : (
                      <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 28, color: '#9ca3af' }}>
                        {TYPE_ICON[item.media_type] || <PictureOutlined />}
                      </div>
                    )}
                    {/* Type badge */}
                    <div style={{
                      position: 'absolute', top: 6, right: 6,
                      background: 'rgba(0,0,0,0.55)', borderRadius: 6,
                      padding: '2px 6px', fontSize: 10, color: '#fff', fontWeight: 700,
                    }}>
                      {item.media_type === 'video' ? '▶ Video' : item.media_type === 'document' ? '📄 Doc' : item.media_type === 'album' ? '🗂 Album' : '🖼'}
                    </div>
                  </div>
                  {/* Info */}
                  <div style={{ padding: '8px 10px' }}>
                    <div style={{ fontSize: 12, fontWeight: 700, color: '#111827', lineHeight: 1.3 }}>{item.title_hi}</div>
                    {item.category && (
                      <div style={{ marginTop: 4, fontSize: 10, color: '#6b7280', textTransform: 'capitalize', fontWeight: 600 }}>{item.category}</div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Load more */}
          {items.length < total && (
            <div style={{ textAlign: 'center', marginTop: 18 }}>
              <button
                onClick={() => load(page + 1)}
                disabled={loading}
                style={{
                  background: M, color: '#FDE68A', border: 'none',
                  borderRadius: 10, padding: '10px 28px', fontSize: 13, fontWeight: 700,
                  cursor: loading ? 'not-allowed' : 'pointer', opacity: loading ? 0.6 : 1,
                }}>
                {loading ? t('लोड हो रहा है…', 'Loading…') : t('और देखें', 'Load More')}
              </button>
            </div>
          )}
        </>
      )}

      {/* ── Item Viewer Modal ── */}
      {active && (
        <Modal
          open={!!active}
          title={active.title_hi}
          onCancel={() => setActive(null)}
          footer={null}
          width={680}
          centered
          destroyOnClose
          bodyStyle={{ padding: '12px 16px 20px' }}
        >
          {/* Embed */}
          {active.media_type === 'image' ? (
            <div style={{ textAlign: 'center', marginBottom: 12 }}>
              <img
                src={driveEmbed(active.drive_link, 'image')}
                alt={active.title_hi}
                style={{ maxWidth: '100%', maxHeight: 400, borderRadius: 8 }}
              />
            </div>
          ) : (
            <iframe
              src={driveEmbed(active.drive_link, active.media_type)}
              width="100%"
              height="380"
              allow="autoplay"
              style={{ border: 'none', borderRadius: 8, marginBottom: 12 }}
              title={active.title_hi}
            />
          )}

          {/* Description */}
          {active.description_hi && (
            <div style={{ fontSize: 13, color: '#374151', marginBottom: 12 }}>{active.description_hi}</div>
          )}

          {/* Action buttons */}
          <div style={{ display: 'flex', gap: 10 }}>
            <a
              href={active.drive_link}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                background: M, color: '#FDE68A', borderRadius: 10, padding: '10px 0',
                fontWeight: 700, fontSize: 13, textDecoration: 'none',
              }}
            >
              <DownloadOutlined /> {t('Drive में खोलें / डाउनलोड', 'Open / Download')}
            </a>
            {active.media_type !== 'folder' && active.media_type !== 'album' && (
              <a
                href={`https://drive.google.com/uc?export=download&id=${parseDriveId(active.drive_link)}`}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                  background: '#f9fafb', color: '#374151', border: '1px solid #e5e7eb',
                  borderRadius: 10, padding: '10px 18px', fontWeight: 600, fontSize: 12, textDecoration: 'none',
                }}
              >
                <DownloadOutlined /> {t('डाउनलोड', 'Download')}
              </a>
            )}
          </div>
        </Modal>
      )}
    </div>
  );
}
